import React from 'react';
import { Page, InputField, InputRow } from '@parchment/ui-primitives';
import styled from 'styled-components';
import { alto, apple, Anchor, Banner, blueLagoon, dustGray, emperor, Heading1,
    Heading2, mercury, white, wildSand } from '@parchment/atoms';
import { RequiredAsterisk } from '@parchment/forms';

export default class AccountConfirmationPage extends Page {
    static get propTypes() {
        return Object.assign({}, super.propTypes, {
        });
    }

    static get defaultProps() {
        return Object.assign({}, super.defaultProps, {
            title: 'Account Confirmation'
        });
    }

    get onCancel() {
        return () => {
            this.props.logoutNewUser().then(() =>
                this.props.router.push(`/u/registration/${this.props.params.memberId}/institution#/tpo`));
        };
    }

    constructor(props) {
        super(props);
        this.handleResendEmailLink = this.handleResendEmailLink.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        const code = props.params.code || '';
        const { email=(window.config.email||''), id=window.config.individualId, individualEmailId=window.config.individualEmailId } = props.user || {};
        this.state = Object.assign({}, this.state, {
            code,
            email,
            individualId: id,
            individualEmailId,
            showBanner: false,
            context: 'error',
            bannerMessage: ''
        });
    }

    getPageClass() {
        return 'narrow-width';
    }

    handleResendEmailLink(e) {
        e.preventDefault();
        e.stopPropagation();

        // resend email
        const individualEmailId = this.state.individualEmailId;
        if(individualEmailId) {
            this.props.verifyUser({individualEmailId}).then(() => {
                this.setState({
                    showBanner: true,
                    context: 'success',
                    bannerMessage: 'Successfully resend verification email'
                });
            });
        } else {
            this.setState({
                showBanner: true,
                context: 'error',
                bannerMessage: 'No user to verify'
            });
        }
    }

    handleSubmit() {
        const params = {
            individualId: this.state.individualId,
            email: this.state.email,
            code: this.code
        };

        const verifyFailed = (error) => {
            var errorMessage = 'Failed to verify a parchment account, please try again';
            if (error) {
                errorMessage = 'Failed to verify a parchment account. ' + error;
            }
            this.setState({
                showBanner: true,
                context: 'error',
                bannerMessage: errorMessage
            });
        };

        this.props.processConfirmation(params)
            .then((resp) => {
                if(resp.payload.data.code === 200) {
                    if(this.props.params && this.props.params.memberId) {
                        window.location.href = `/u/auth/login?memberId=${this.props.params.memberId}`;
                    } else {
                        window.location.href = '/u/auth/login';
                    }
                } else {
                    verifyFailed(resp.payload.data.data.message);
                }
            })
            .catch(err => {
                let error = '';
                try {
                    error = err.response.data.errors[0];
                } catch (error) {}
                verifyFailed(error);
            });
    }

    get valid() {
        // This is to make the field not show invalid.
        return this.code !== ''||undefined;
    }

    get code() {
        return this.state.code;
    }

    set code(value) {
        this.setState({code: value});
    }

    getBody() {
        return (
            <AccountConfirmationPageContainer className='account-confirmation-page-container'>
                <AccountConfirmationInputSection className='account-confirmation-input-section'>
                    <ConfirmationTitleText className='account-confirmation-title-text'>
                        <StyledHeading2>To create your account, we need to confirm a</StyledHeading2>
                        <StyledHeading1>valid email address</StyledHeading1>
                    </ConfirmationTitleText>
                    <ConfirmationIcon className='account-confirmation-icon'>
                        <img src='/u/asset/Parchment.Registration/img/email-verification-icon.png' aria-hidden='true' />
                    </ConfirmationIcon>
                    <div className='account-confirmation-info-header'>
                        <StyledHeading1>We just sent you a confirmation email.</StyledHeading1>
                        <CheckEmailText className='check-email-text'>
                            To gain access to your account, please check <strong>{this.state.email} </strong>
                            and enter the provided registration code below.
                        </CheckEmailText>
                    </div>
                </AccountConfirmationInputSection>
                <InputSection className="account-confirmation-code-container">
                    <InputRow>
                        <InputField value={this.code}
                                    onChange={(code) => this.code = code}
                                    valid={this.valid}
                                    required={this.valid}
                                    labelAsPlaceHolder={true}
                                    label="Confirmation Code"
                                    className="col-xs-9"/>
                        <div className="form-group input-field col-xs-3">
                            <ConfirmationButton className="account-confirmation-code-button form-control"
                                                disabled={!this.valid}
                                                onClick={this.handleSubmit}>
                                SUBMIT
                            </ConfirmationButton>
                        </div>
                    </InputRow>
                </InputSection>
                <div className='account-confirmation-action-section'>
                    {
                        this.state.showBanner &&
                            <Banner visible={this.state.showBanner}
                                    title={this.state.context}
                                    context={this.state.context}
                                    dismissable={true}
                                    size='large'
                                    message={this.state.bannerMessage}
                                    onDismiss={() => {
                                        this.setState({
                                            showBanner: false,
                                            bannerMessage: ''
                                        });
                                    }} />
                    }
                    <EmailSupportText className='email-support-text'>
                        Please check your spam folder if you don't see the email. Having trouble?
                    </EmailSupportText>
                    <SupportContentWrapper className='email-support-content'>
                        <div>
                            <Anchor onClick={this.handleResendEmailLink}>
                                Re-send My Verification Email
                            </Anchor>
                        </div>
                        <div><Anchor href="/">Start Over</Anchor></div>
                        <div><Anchor href="/support">Parchment Support</Anchor></div>
                    </SupportContentWrapper>
                    <StyledRequiredSection>
                        <RequiredAsterisk />
                    </StyledRequiredSection>
                </div>
            </AccountConfirmationPageContainer>);
    }
}

const AccountConfirmationInputSection = styled.div`
    background-color: ${white};
`;

const ConfirmationTitleText = styled.div`
    padding-top: 60px;
`;

const StyledHeading1 = styled(Heading1)`
    margin: 0 auto;
    font-weight: 600;
    color: ${blueLagoon};
    font-size: 24px;
    text-align: center;
    line-height: 35px;
`;

const StyledHeading2 = styled(Heading2)`
    margin: 0 auto;
    font-weight: 100;
    font-size: 20px;
    text-align: center;
    line-height: 30px;
`;

const ConfirmationIcon = styled.div`
    margin: 0 auto;
    width: 164px;
`;

const CheckEmailText = styled.div`
    margin: 0 auto;
    text-align: center;
    width: 600px;
    padding: 20px 0;
`;

const InputSection = styled.div`
    background-color: ${white};
    padding: 30px 0 60px 0;

    .input-row {
        margin: 0 auto;
        width: 400px;
    }
`;

const ConfirmationButton = styled.button`
    background-color: ${apple};
    border: 0;
    height: 50px;
    font-weight: 600;

    &:disabled {
        background-color: ${mercury};
    }

    button {
        color: ${white};
    }
`;

const EmailSupportText = styled.div`
    text-align: center;
    padding-top: 60px;
`;

const SupportContentWrapper = styled.div`
    display: flex;
    justify-content: center;
    flex-direction: row;
    flex-wrap: wrap;
    font-weight: 600;
    margin-top: 5px;

    > div {
        padding: 0 10px;
    }

    > div:not(:first-child) {
        border-left: 1px solid ${dustGray};
    }
`;

const AccountConfirmationPageContainer = styled.div`
    color: ${emperor};
    background-color: ${wildSand};
    border: 1px solid ${alto};
    padding: 0;
    margin: 0;
    font-size: 14px;
    line-height: 20px;
}`;

const StyledRequiredSection = styled.div`
    padding: 40px 0;

    > div {
        justify-content: center;
    }
`;
